import { useState, useRef } from "react";
import { Copy, Check } from "lucide-react";
import { motion } from "framer-motion";

/* Standalone copy-to-clipboard URL chip with marquee-on-hover: the URL scrolls
   horizontally while hovered and a copy icon copies it (no navigation). Vendored
   into the takedown dir so it carries no recall/ dependency. */
const CopyableUrl = ({ value, className = "" }) => {
  const [copied, setCopied] = useState(false);
  const [overflowPx, setOverflowPx] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const textRef = useRef(null);
  const viewportRef = useRef(null);

  if (!value) return null;

  const shouldAnimate = isHovering && overflowPx > 0;

  const handleMouseEnter = () => {
    const t = textRef.current;
    const v = viewportRef.current;
    if (!t || !v) return;
    const overflow = t.scrollWidth - v.clientWidth;
    setOverflowPx(Math.max(0, overflow));
    setIsHovering(true);
  };

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-md px-0.5 min-w-0 max-w-full ${className}`}
    >
      <span
        ref={viewportRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={() => setIsHovering(false)}
        className="overflow-hidden min-w-0"
        title={value}
      >
        <motion.span
          ref={textRef}
          className="text-xs text-velBlue/80 whitespace-nowrap inline-block"
          animate={{ x: shouldAnimate ? -overflowPx : 0 }}
          transition={
            shouldAnimate
              ? {
                  duration: Math.max(1.5, overflowPx / 50),
                  repeat: Infinity,
                  repeatType: "reverse",
                  ease: "linear",
                }
              : { duration: 0.3, ease: "easeOut" }
          }
        >
          {value}
        </motion.span>
      </span>
      <button
        onClick={(e) => {
          e.stopPropagation();
          navigator.clipboard.writeText(value);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        }}
        className="shrink-0 text-mutedText hover:text-velBlue transition-colors"
        aria-label="Copy URL"
      >
        {copied ? <Check size={10} /> : <Copy size={10} />}
      </button>
    </span>
  );
};

export default CopyableUrl;
